import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ThumbsUp, RefreshCw, Plus, Edit2, Trash2, CheckCircle2, XCircle, ImageIcon, Gift } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_PILL = {
  feedback_asked: { label: 'Feedback asked', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  task_sent: { label: 'Task sent', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
  screenshot_received: { label: 'Pending review', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  approved: { label: 'Approved · Coupon', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-800 border-red-200' },
  low_feedback_closed: { label: 'Low rating', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  rated_no_task: { label: 'Rated · no task left', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
  asked: { label: 'Review asked (legacy)', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  verified: { label: 'Coupon issued (legacy)', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
  } catch (_e) { return iso; }
};

const screenshotSrc = (r) => {
  if (!r.screenshot_url) return null;
  return r.screenshot_url.startsWith('/api/') ? `${API}${r.screenshot_url}` : r.screenshot_url;
};

const EMPTY_TASK = { name: '', description: '', message_text: '', task_url: '', default_coupon_amount: 100, coupon_amount_ameesha: 100, coupon_amount_eden: 100, active: true };

export default function PatientFeedbackPage({ user, onLogout }) {
  const isAdmin = user?.role === 'Admin';
  const [tab, setTab] = useState('pending');
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [approving, setApproving] = useState(null); // event row
  const [approveAmount, setApproveAmount] = useState(100);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editingTask, setEditingTask] = useState(null); // null | task row | EMPTY_TASK
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab === 'pending' ? { status: 'screenshot_received' } : (statusFilter ? { status: statusFilter } : {});
      const [ev, tk] = await Promise.all([
        api.get('/feedback/events', { params: { ...params, limit: 200 } }),
        api.get('/feedback/reward-tasks'),
      ]);
      setEvents(ev.data?.rows || []);
      setCounts(ev.data?.status_counts || {});
      setTasks(tk.data?.rows || []);
    } catch (_e) {
      toast.error('Failed to load patient feedback');
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const doApprove = async () => {
    if (!approving) return;
    setBusy(true);
    try {
      const endpoint = approving.status === 'task_sent'
        ? `/feedback/events/${approving.id}/manual-coupon`
        : `/feedback/events/${approving.id}/approve`;
      const res = await api.post(endpoint, { amount: parseInt(approveAmount, 10) });
      toast.success(`Coupon ${res.data?.coupon?.code || ''} issued (₹${approveAmount})${approving.status === 'task_sent' ? ' — process bypassed' : ''}`);
      setApproving(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!rejecting) return;
    setBusy(true);
    try {
      await api.post(`/feedback/events/${rejecting.id}/reject`, { reason: rejectReason });
      toast.success('Submission rejected — task marked complete');
      setRejecting(null);
      setRejectReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    } finally { setBusy(false); }
  };

  const resendTask = async (r) => {
    if (!window.confirm(`Resend the "${r.reward_task_name || 'task'}" WhatsApp message to ${r.patient_name}?`)) return;
    try {
      const res = await api.post(`/feedback/events/${r.id}/resend-task`);
      if (res.data?.sent) toast.success('Task message resent on WhatsApp');
      else toast.error(res.data?.error || 'Send failed — check template status');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    }
  };

  const saveTask = async () => {
    if (!editingTask?.name || !editingTask?.message_text) {
      toast.error('Name and task message are required');
      return;
    }
    setBusy(true);
    try {
      const body = {
        name: editingTask.name,
        description: editingTask.description || '',
        message_text: editingTask.message_text,
        task_url: editingTask.task_url || '',
        default_coupon_amount: parseInt(editingTask.coupon_amount_ameesha || editingTask.default_coupon_amount, 10) || 100,
        coupon_amount_ameesha: parseInt(editingTask.coupon_amount_ameesha, 10) || 100,
        coupon_amount_eden: parseInt(editingTask.coupon_amount_eden, 10) || 100,
        order: editingTask.order,
        active: !!editingTask.active,
      };
      if (editingTask.id) await api.put(`/feedback/reward-tasks/${editingTask.id}`, body);
      else await api.post('/feedback/reward-tasks', body);
      toast.success('Reward task saved');
      setEditingTask(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const deleteTask = async (t) => {
    if (!window.confirm(`Delete reward task "${t.name}"?`)) return;
    try {
      await api.delete(`/feedback/reward-tasks/${t.id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const pendingCount = counts['screenshot_received'] || 0;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="patient-feedback-page">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ThumbsUp className="w-6 h-6 text-amber-600" /> Patient Feedback
          </h1>
          <button onClick={load} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg" data-testid="feedback-refresh-button">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 flex-wrap">
          <Button variant={tab === 'pending' ? 'default' : 'outline'} size="sm" onClick={() => setTab('pending')} data-testid="tab-pending-review">
            Pending Review {pendingCount > 0 && <Badge className="ml-2 bg-indigo-600 text-white">{pendingCount}</Badge>}
          </Button>
          <Button variant={tab === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setTab('all')} data-testid="tab-all-activity">
            All Activity
          </Button>
          <Button variant={tab === 'tasks' ? 'default' : 'outline'} size="sm" onClick={() => setTab('tasks')} data-testid="tab-reward-tasks">
            Reward Tasks ({tasks.length})
          </Button>
        </div>

        {/* ===== Events (pending / all) ===== */}
        {(tab === 'pending' || tab === 'all') && (
          <>
            {tab === 'all' && (
              <div className="mb-4">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="feedback-status-filter"
                >
                  <option value="">All statuses</option>
                  {Object.entries(STATUS_PILL).map(([k, v]) => (
                    <option key={k} value={k}>{v.label} ({counts[k] || 0})</option>
                  ))}
                </select>
              </div>
            )}
            {events.length === 0 && !loading && (
              <Card className="p-8 text-center text-gray-500 text-sm" data-testid="feedback-empty-state">
                {tab === 'pending' ? 'No screenshots waiting for review.' : 'No feedback activity yet.'}
              </Card>
            )}
            <div className="space-y-3">
              {events.map((r) => {
                const pill = STATUS_PILL[r.status] || { label: r.status, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
                const src = screenshotSrc(r);
                return (
                  <Card key={r.id} className="p-4" data-testid={`feedback-event-${r.id}`}>
                    <div className="flex flex-wrap items-start gap-4">
                      <div className="flex-1 min-w-[220px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900">{r.patient_name || 'Unknown patient'}</span>
                          <span className="text-xs text-gray-500">{r.patient_phone}</span>
                          <Badge className={`text-xs ${pill.cls}`}>{pill.label}</Badge>
                          {r.feedback_score && <Badge variant="outline" className="text-xs">Score {r.feedback_score >= 5 ? '5' : '4 or less'}</Badge>}
                          <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{r.profile}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {r.source_service_name}{r.source_appointment_ref ? ` · ${r.source_appointment_ref}` : ''}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 space-x-2">
                          <span>Asked {fmtWhen(r.feedback_asked_at)}</span>
                          {r.reward_task_name && <span>· Task: <strong>{r.reward_task_name}</strong></span>}
                          {r.screenshot_received_at && <span>· Screenshot {fmtWhen(r.screenshot_received_at)}</span>}
                          {r.approved_at && <span>· Approved {fmtWhen(r.approved_at)} (₹{r.coupon_amount})</span>}
                          {r.rejected_at && <span>· Rejected {fmtWhen(r.rejected_at)}{r.reject_reason ? ` — ${r.reject_reason}` : ''}</span>}
                        </div>
                      </div>
                      {src && (
                        <a href={src} target="_blank" rel="noreferrer" className="shrink-0" data-testid={`screenshot-link-${r.id}`}>
                          <img src={src} alt="screenshot" className="w-20 h-20 object-cover rounded-lg border border-gray-200" />
                        </a>
                      )}
                      {r.status === 'screenshot_received' && (
                        <div className="flex gap-2 shrink-0">
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => { setApproving(r); setApproveAmount(r.reward_default_amount || 100); }}
                            data-testid={`approve-button-${r.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => { setRejecting(r); setRejectReason(''); }}
                            data-testid={`reject-button-${r.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                      {r.status === 'task_sent' && (
                        <div className="flex gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-amber-700 border-amber-300 hover:bg-amber-50"
                            onClick={() => resendTask(r)}
                            data-testid={`resend-task-button-${r.id}`}
                          >
                            Resend Task{r.task_resend_count ? ` (${r.task_resend_count})` : ''}
                          </Button>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => { setApproving(r); setApproveAmount(r.reward_default_amount || 100); }}
                            data-testid={`send-coupon-button-${r.id}`}
                          >
                            <Gift className="w-4 h-4 mr-1" /> Send Coupon
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {/* ===== Reward Tasks config ===== */}
        {tab === 'tasks' && (
          <>
            {isAdmin && (
              <div className="mb-4">
                <Button size="sm" onClick={() => setEditingTask({ ...EMPTY_TASK })} data-testid="add-reward-task-button">
                  <Plus className="w-4 h-4 mr-1" /> Add Reward Task
                </Button>
              </div>
            )}
            {tasks.length === 0 && (
              <Card className="p-8 text-center text-gray-500 text-sm" data-testid="reward-tasks-empty">
                No reward tasks configured yet. {isAdmin ? 'Add the first task (e.g. Google Review) to activate the cascade.' : 'Ask an admin to configure them.'}
              </Card>
            )}
            <div className="space-y-3">
              {tasks.map((t) => (
                <Card key={t.id} className="p-4" data-testid={`reward-task-${t.id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <Badge variant="outline" className="text-xs mt-0.5">#{t.order}</Badge>
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{t.name}</span>
                        <Badge className={t.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                          {t.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      {t.description && <div className="text-xs text-gray-500 mt-1">{t.description}</div>}
                      {t.message_text && (
                        <div className="text-xs text-gray-600 mt-1 italic">"{t.message_text}"</div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        Sent via <code className="bg-gray-100 px-1 rounded">eden_task_generic</code>
                        {' '}· Coupon ₹{t.coupon_amount_ameesha ?? t.default_coupon_amount} (Ameesha) / ₹{t.coupon_amount_eden ?? t.default_coupon_amount} (Eden)
                        {t.task_url && <> · <a href={t.task_url} target="_blank" rel="noreferrer" className="text-sky-600 underline">task link</a></>}
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditingTask({ ...t })} data-testid={`edit-task-${t.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteTask(t)} data-testid={`delete-task-${t.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* ===== Approve dialog ===== */}
        <Dialog open={!!approving} onOpenChange={(o) => !o && setApproving(null)}>
          <DialogContent data-testid="approve-dialog">
            <DialogHeader>
              <DialogTitle>Approve & issue coupon</DialogTitle>
            </DialogHeader>
            {approving && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Issue a discount coupon to <strong>{approving.patient_name}</strong> for completing{' '}
                  <strong>{approving.reward_task_name || 'the task'}</strong>. Coupon is sent on WhatsApp instantly.
                </p>
                {approving.status === 'task_sent' && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="manual-coupon-note">
                    No screenshot has been received — this bypasses verification and marks the task complete.
                  </p>
                )}
                <div>
                  <Label>Coupon amount (₹)</Label>
                  <Input
                    type="number"
                    value={approveAmount}
                    onChange={(e) => setApproveAmount(e.target.value)}
                    className="mt-1"
                    data-testid="approve-amount-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setApproving(null)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={doApprove} data-testid="approve-confirm-button">
                {busy ? 'Issuing…' : `Issue ₹${approveAmount || 0} coupon`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Reject dialog ===== */}
        <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
          <DialogContent data-testid="reject-dialog">
            <DialogHeader>
              <DialogTitle>Reject submission</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600">
              The task is still marked complete for the patient (no coupon issued). They'll receive the next task in the cascade on their next positive rating.
            </p>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="mt-1" data-testid="reject-reason-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
              <Button variant="destructive" disabled={busy} onClick={doReject} data-testid="reject-confirm-button">
                {busy ? 'Rejecting…' : 'Reject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Task editor dialog ===== */}
        <Dialog open={!!editingTask} onOpenChange={(o) => !o && setEditingTask(null)}>
          <DialogContent data-testid="task-editor-dialog">
            <DialogHeader>
              <DialogTitle>{editingTask?.id ? 'Edit Reward Task' : 'New Reward Task'}</DialogTitle>
            </DialogHeader>
            {editingTask && (
              <div className="space-y-3">
                <div>
                  <Label>Task name *</Label>
                  <Input value={editingTask.name} onChange={(e) => setEditingTask({ ...editingTask, name: e.target.value })} placeholder="e.g. Google Review" className="mt-1" data-testid="task-name-input" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={editingTask.description || ''} onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })} placeholder="What the patient must do" className="mt-1" data-testid="task-description-input" />
                </div>
                <div>
                  <Label>Task message (sent to patient) *</Label>
                  <Textarea
                    value={editingTask.message_text || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, message_text: e.target.value })}
                    placeholder="e.g. Leave us a Google review at https://maps.app.goo.gl/… and earn a discount voucher on your next visit"
                    className="mt-1"
                    data-testid="task-message-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">Goes into {'{{2}}'} of the shared eden_task_generic template. Include the task link here. Keep it a single line — WhatsApp doesn't allow line breaks in variables.</p>
                </div>
                <div>
                  <Label>Task link (reference)</Label>
                  <Input value={editingTask.task_url || ''} onChange={(e) => setEditingTask({ ...editingTask, task_url: e.target.value })} placeholder="https://maps.app.goo.gl/…" className="mt-1" data-testid="task-url-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Coupon ₹ — Dr Ameesha feedback</Label>
                    <Input type="number" value={editingTask.coupon_amount_ameesha ?? editingTask.default_coupon_amount ?? 100} onChange={(e) => setEditingTask({ ...editingTask, coupon_amount_ameesha: e.target.value })} className="mt-1" data-testid="task-amount-ameesha-input" />
                  </div>
                  <div>
                    <Label>Coupon ₹ — Eden feedback</Label>
                    <Input type="number" value={editingTask.coupon_amount_eden ?? editingTask.default_coupon_amount ?? 100} onChange={(e) => setEditingTask({ ...editingTask, coupon_amount_eden: e.target.value })} className="mt-1" data-testid="task-amount-eden-input" />
                  </div>
                </div>
                <div>
                  <Label>Order</Label>
                  <Input type="number" value={editingTask.order || ''} onChange={(e) => setEditingTask({ ...editingTask, order: e.target.value ? parseInt(e.target.value, 10) : null })} placeholder="auto" className="mt-1" data-testid="task-order-input" />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editingTask.active} onChange={(e) => setEditingTask({ ...editingTask, active: e.target.checked })} data-testid="task-active-checkbox" />
                  Active
                </label>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingTask(null)}>Cancel</Button>
              <Button disabled={busy} onClick={saveTask} data-testid="task-save-button">{busy ? 'Saving…' : 'Save Task'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
